import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Link2, Linkedin } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { Empty, FilterBar, fmtDate } from "./EnquiriesPanel";

const STATUSES = ["new", "reviewed", "shortlisted", "rejected"];
const FILTERS = ["all", ...STATUSES];
const STATUS_CLS = { new: "bg-brand-cyan/15 text-brand-indigo", reviewed: "bg-amber-50 text-amber-700", shortlisted: "bg-emerald-50 text-emerald-700", rejected: "bg-slate-100 text-slate-500" };

export const ApplicationsPanel = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/applications").then((r) => setItems(r.data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const shown = useMemo(() => (items || []).filter((a) => filter === "all" || a.status === filter), [items, filter]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f, f === "all" ? items?.length || 0 : items?.filter((a) => a.status === f).length || 0])), [items]);

  const setStatus = async (a, status) => {
    try {
      const { data } = await api.patch(`/applications/${a.id}`, { status });
      setItems((list) => list.map((x) => (x.id === a.id ? data : x)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application from ${a.name}?`)) return;
    try {
      await api.delete(`/applications/${a.id}`);
      setItems((list) => list.filter((x) => x.id !== a.id));
      toast.success("Application deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-applications-panel">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">Applications</h2>
        <FilterBar filters={FILTERS} value={filter} onChange={setFilter} counts={counts} testPrefix="applications" />
      </div>
      <div className="mt-8 space-y-4">
        {items === null && <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-indigo/60">Loading applications…</div>}
        {items && shown.length === 0 && <Empty testId="applications-empty" text={`No ${filter === "all" ? "" : filter} applications yet.`} />}
        {shown.map((a) => (
          <article key={a.id} data-testid={`application-row-${a.id}`} className="rounded-2xl border border-brand-indigo/15 bg-white p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-lg font-semibold text-brand-ink">{a.name}</h3>
                  <span className="rounded-full border border-brand-indigo/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/70">{a.job_title}</span>
                  <span data-testid={`application-status-${a.id}`} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_CLS[a.status]}`}>{a.status}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                  <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-indigo"><Mail className="h-3.5 w-3.5" />{a.email}</a>
                  <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-brand-indigo"><Phone className="h-3.5 w-3.5" />{a.phone}</a>
                  <a data-testid={`application-resume-${a.id}`} href={a.resume_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-indigo hover:underline"><Link2 className="h-3.5 w-3.5" />Resume / portfolio</a>
                  {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-indigo hover:underline"><Linkedin className="h-3.5 w-3.5" />LinkedIn</a>}
                  <span>{fmtDate(a.created_at)}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid={`application-status-select-${a.id}`} value={a.status} onChange={(e) => setStatus(a, e.target.value)} className="rounded-full border border-brand-indigo/20 bg-white px-4 py-2 text-xs font-semibold text-brand-indigo outline-none">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`application-delete-${a.id}`} onClick={() => remove(a)} className="grid h-9 w-9 place-items-center rounded-full border border-red-200 text-red-500 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{a.cover}</p>
          </article>
        ))}
      </div>
    </div>
  );
};
